import React, { useState } from 'react';
import {
  Card,
  Col,
  Empty,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import { useUsageAdoption } from '../../hooks/useUsageAdoption';
import { UsageBusinessAdoptionRow } from '../../types/usageTracking';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const AdoptionUsagePage: React.FC = () => {
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const { data: rows = [], isLoading, isFetching } = useUsageAdoption(days);

  const activeBusinesses = rows.filter((r) => r.activeDays > 0).length;
  const totalLogins = rows.reduce((s, r) => s + r.logins, 0);
  const totalPos = rows.reduce((s, r) => s + r.posSales, 0);
  const totalClients = rows.reduce((s, r) => s + r.clientsCreated, 0);

  const columns: ColumnsType<UsageBusinessAdoptionRow> = [
    {
      title: 'Business',
      dataIndex: 'businessName',
      key: 'businessName',
      sorter: (a, b) => a.businessName.localeCompare(b.businessName, 'fr'),
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.businessId}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Dernière activité',
      dataIndex: 'lastEventAt',
      key: 'lastEventAt',
      sorter: (a, b) => (a.lastEventAt?.getTime() ?? 0) - (b.lastEventAt?.getTime() ?? 0),
      defaultSortOrder: 'descend',
      render: (value: Date | null) =>
        value ? (
          <Space direction="vertical" size={0}>
            <Text>{dayjs(value).fromNow()}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(value).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Space>
        ) : (
          <Tag>Aucune</Tag>
        ),
    },
    {
      title: 'Jours actifs',
      dataIndex: 'activeDays',
      key: 'activeDays',
      width: 110,
      sorter: (a, b) => a.activeDays - b.activeDays,
    },
    {
      title: 'Connexions',
      dataIndex: 'logins',
      key: 'logins',
      width: 110,
      sorter: (a, b) => a.logins - b.logins,
    },
    {
      title: 'Ventes POS',
      dataIndex: 'posSales',
      key: 'posSales',
      width: 110,
      sorter: (a, b) => a.posSales - b.posSales,
    },
    {
      title: 'Clients',
      dataIndex: 'clientsCreated',
      key: 'clientsCreated',
      width: 90,
      sorter: (a, b) => a.clientsCreated - b.clientsCreated,
    },
    {
      title: 'RDV',
      dataIndex: 'appointmentsCreated',
      key: 'appointmentsCreated',
      width: 80,
      sorter: (a, b) => a.appointmentsCreated - b.appointmentsCreated,
    },
    {
      title: 'Stock',
      dataIndex: 'stockMovements',
      key: 'stockMovements',
      width: 90,
      sorter: (a, b) => a.stockMovements - b.stockMovements,
    },
    {
      title: 'Modules',
      key: 'modules',
      render: (_, row) =>
        row.topModules.length === 0 ? (
          <Text type="secondary">—</Text>
        ) : (
          <Space wrap size={[4, 4]}>
            {row.topModules.map((m) => (
              <Tag key={m.module} color="purple">
                {m.module} ({m.count})
              </Tag>
            ))}
          </Space>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ color: '#d946ef', margin: 0 }}>
            <LineChartOutlined style={{ marginRight: 10 }} />
            Adoption & usage
          </Title>
          <Text style={{ color: '#666', fontSize: 14, display: 'block', marginTop: 8 }}>
            Activité des employés par business (événements métier + modules visités).
          </Text>
        </div>
        <Segmented
          value={days}
          onChange={(v) => setDays(v as 7 | 14 | 30)}
          options={[
            { label: '7 jours', value: 7 },
            { label: '14 jours', value: 14 },
            { label: '30 jours', value: 30 },
          ]}
        />
      </div>

      <Card style={{ marginBottom: 24, borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Statistic title="Businesses actifs" value={activeBusinesses} valueStyle={{ color: '#52c41a' }} />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic title="Connexions" value={totalLogins} valueStyle={{ color: '#1890ff' }} />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic title="Ventes POS" value={totalPos} valueStyle={{ color: '#d946ef' }} />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic title="Nouveaux clients" value={totalClients} valueStyle={{ color: '#faad14' }} />
          </Col>
        </Row>
      </Card>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : rows.length === 0 ? (
        <Empty description="Aucune donnée d'usage pour le moment" />
      ) : (
        <Table
          rowKey="businessId"
          columns={columns}
          dataSource={rows}
          loading={isFetching}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1100 }}
        />
      )}
    </div>
  );
};

export default AdoptionUsagePage;
